/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { HaproxyStatsApiService } from '../../http';
import { Injectable, OnDestroy } from '@angular/core';
import { LogService, SocketService, UtilsService } from '../../../../../core/services';
import { Subject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class WidgetService implements OnDestroy {
    /**
     * HAProxy data emitter.
     */
    public haDataEmitter: Subject<any> = new Subject<any>();

    /**
     * System status emitter.
     */
    public statusDataEmitter: Subject<any> = new Subject<any>();

    /**
     * Send subscription confirm.
     */
    public sendSubscriptionConfirmEmitter: Subject<any> = new Subject<any>();

    /**
     * Receive subscription confirm.
     */
    public receiveSubscriptionConfirmEmitter: Subject<any> = new Subject<any>();

    /**
     * Socket closed.
     */
    public socketClosedEmitter: Subject<any> = new Subject<any>();

    /**
     * Store haproxy data.
     * @private
     */
    private haproxyData: any;

    /**
     * Store system stats data.
     * @private
     */
    private systemStatsData: any;

    /**
     * Stored Timestamp of the last socket event.
     * @private
     */
    private timeStamp: number;

    /**
     * Stored intervalId for use in OnDestroy.
     * @private
     */
    private intervalId: any;

    constructor(
        private haproxyStatsApiService: HaproxyStatsApiService,
        private logService: LogService,
        private socketService: SocketService,
        private utilsService: UtilsService
    ) {
        this.onInitialise();
        this.socketListeners();
        this.confirmSubscriptionSubscriber();
    }

    onInitialise(): void {
        this.timeStamp = new Date().getTime();
        this.intervalId = setInterval((): void => {
            if (Math.abs(this.timeStamp - new Date().getTime()) > 15000) {
                this.getDataFromApi();
                this.logService.debug('WidgetService:onInitialise', 'Socket data out of date, making an API call.');
            }
        }, 15000);
    }

    ngOnDestroy(): void {
        this.tearDownService();
    }

    /**
     * Setup socket listener.
     */
    private socketListeners(): void {
        this.socketService.emit('systemStats', 'stats');
        this.socketService.emit('haproxy', 'stats');

        this.socketService.on('request')
            .subscribe({
                next: (response: any): void => {
                    const data = response.data;
                    if (data?.action === 'subscriptionConfirm') {
                        this.sendSubscriptionConfirmEmitter.next(data.service);
                    }
                }
            });

        this.socketService.on('stats')
            .subscribe({
                next: (response: any): void => {
                    this.timeStamp = new Date().getTime();
                    const data = response.data;
                    if (data.hasOwnProperty('system')) {
                        if (!this.utilsService.compareData(this.systemStatsData, data?.system)) {
                            this.systemStatsData = data?.system;
                            this.statusDataEmitter.next(data?.system);
                        }
                    } else if (data.hasOwnProperty('haproxy')) {
                        if (!this.utilsService.compareData(this.haproxyData, data?.haproxy)) {
                            this.haproxyData = data?.haproxy;
                            this.haDataEmitter.next(data?.haproxy);
                        }
                    }
                }
            });

        this.socketService.socketClosedEmitter
            .subscribe({
                next: (hasClosed: any): void => {
                    this.socketClosedEmitter.next(hasClosed);
                    if (hasClosed) {
                        this.getDataFromApi();
                    }
                }
            });
    }

    /**
     * Get data from api if websocket has closed.
     * @private
     */
    private getDataFromApi(): void {
        // TODO: There is no api endpoint for system stats so for now just get haproxy data.
        this.haproxyStatsApiService.getAllStats()
            .subscribe({
                next: (response: any): void => {
                    this.haproxyData = response;
                    this.haDataEmitter.next(response);
                }
            });
    }

    /**
     * Confirm subscription.
     * @private
     */
    private confirmSubscriptionSubscriber(): void {
        this.receiveSubscriptionConfirmEmitter
            .subscribe({
                next: (response: any): void => {
                    if (response.service && response.event) {
                        this.socketService.emit(response.service, 'stats');
                    }
                }
            });
    }

    /**
     * Tear down service.
     * @private
     */
    private tearDownService(): void {
        clearInterval(this.intervalId);
        this.logService.debug('WidgetService:tearDownService', 'Service cleaned up');
    }
}
